import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/** PWA install banner — surfaces the browser's beforeinstallprompt once per
 *  visit, dismisses for 30 days on close. On iOS, where beforeinstallprompt
 *  doesn't fire, we show a brief "Add to Home Screen" hint on first visit. */
export default function InstallBanner() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem("p2p_install_dismissed") || 0);
    if (Date.now() - dismissedAt < 1000 * 60 * 60 * 24 * 30) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari doesn't fire beforeinstallprompt. Show the hint once.
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && !isStandalone) setShowIosHint(true);

    // Register service worker as part of the install flow so installability fires.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
  };

  const dismiss = () => {
    localStorage.setItem("p2p_install_dismissed", String(Date.now()));
    setPromptEvent(null);
    setShowIosHint(false);
  };

  if (!promptEvent && !showIosHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 pp-card-elevated p-4 flex items-center gap-3 shadow-2xl"
      style={{ bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
    >
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00E676] to-[#007AFF] flex items-center justify-center">
        <Download size={18} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">Install Pick2Play</div>
        <div className="text-xs text-zinc-400 mt-0.5">
          {showIosHint
            ? "Tap Share → Add to Home Screen"
            : "Installs like an app, works offline, fires reminders."}
        </div>
      </div>
      {promptEvent && (
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="pp-btn-primary px-3 py-1.5 rounded-lg text-xs"
        >
          Install
        </button>
      )}
      <button
        onClick={dismiss}
        data-testid="pwa-install-dismiss"
        className="p-1.5 rounded-lg text-zinc-500 hover:text-white"
        title="Dismiss for 30 days"
      >
        <X size={14} />
      </button>
    </div>
  );
}
